import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MerakisService } from '@app/features/merakis/shared/merakis.service';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { WanService } from '@app/features/wan/shared/wan.service';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { PosService } from '@app/shared/models/pos-service.model';
import {
  NetworkState,
  NetworkStateEvaluation,
  NETWORK_STATE,
  WanInterfacesByHostname,
  evaluateNetworkState
} from '@app/shared/utils/network-thresholds';

@Component({
    selector: 'app-lan-health',
    templateUrl: './lan-health.component.html',
    styleUrls: ['./lan-health.component.css'],
    standalone: false
})
export class LanHealthComponent implements OnInit, OnDestroy {

  isLoading: boolean;
  networkState: NetworkState;
  networkStateEvaluation: NetworkStateEvaluation;
  NETWORK_STATE = NETWORK_STATE;
  storeNumber: string = '';
  wanInterfacesByHostname: WanInterfacesByHostname = {};
  wanInterfaceDataAvailable = true;
  merakiSwitchStatuses: MerakiSwitchStatus[] = [];
  portStatuses: any = null;
  fortigate: any = null;
  cradlepoints: Cradlepoint[] = [];
  fortigateGlobalStatistics: any = null;
  primaryVpnStatus: any = null;
  cradlepointLanAvailability: any = null;
  interfaceThroughput: any = null;
  posServices = [];
  isPosServiceOnline: boolean;

  constructor(
    private merakiService: MerakisService,
    private cradlepointService: CradlepointsService,
    private fortigateService: FortigatesService,
    private wanService: WanService,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      if ('storeNumber' in params) {
        this.storeNumber = params['storeNumber'];
        this.getLanHealth();
      }
    });
   }

  ngOnInit() { 
    this.storeNumber = localStorage.getItem('storeNumber')
  }

  ngOnDestroy() { }

  getLanHealth() {
    localStorage.setItem('storeNumber', this.storeNumber);
    this.isLoading = true;
    this.resetView();
    Promise.all(
      [
        this.getMerakiSwitchStatuses(),
        this.getMerakiPortStatuses(),
        this.getFortigate(),
        this.getCradlepoints(),
        this.getGlobalStatistics(),
        this.getPrimaryVpnStatus(),
        this.getCradlepointLanAvailability(),
        this.getInterfaceThroughput(),
        this.getPosServices(),
        this.getWanInterfaces(),
      ]
    ).then(() => {
      this.networkStateEvaluation = evaluateNetworkState({
        fortigates: this.fortigate ? [this.fortigate] : [],
        cradlepoints: this.cradlepoints,
        merakiSwitchStatuses: this.merakiSwitchStatuses,
        wanInterfacesByHostname: this.wanInterfacesByHostname,
        wanInterfaceDataAvailable: this.wanInterfaceDataAvailable,
      });
      this.networkState = this.networkStateEvaluation.state;
      this.checkPosServices();
      this.isLoading = false;
    });
  }

  getMerakiSwitchStatuses() {
    const promise = new Promise((resolve) => {
      this.merakiService.getSwitchStatuses(this.storeNumber).then(response => {
        response.toPromise().then(meraki_switch_statuses_from_response => {
          try {
            meraki_switch_statuses_from_response["message"].forEach(meraki_switch_statuses_json => {
              let meraki_switch_status: MerakiSwitchStatus = meraki_switch_statuses_json;
              this.merakiSwitchStatuses.push(meraki_switch_status);
            });
          } catch (error) {
            console.log("Meraki switch statuses timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getMerakiPortStatuses() {
    const promise = new Promise((resolve) => {
      this.merakiService.getPortStatuses(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            this.portStatuses = response['message'];
          } catch (error) {
            console.log("Meraki port statuses timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getCradlepoints() {
    const promise = new Promise((resolve) => {
      this.cradlepointService.findByStoreNumber(this.storeNumber).then(response => {
        response.toPromise().then(cradlepoints_from_response => {
          try {
            cradlepoints_from_response["message"].forEach(cradlepoint_json => {
              let cradlepoint: Cradlepoint = cradlepoint_json;
              this.cradlepoints.push(cradlepoint);
            });
          } catch (error) {
            console.log("Cradlepoints timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getFortigate() {
    const promise = new Promise((resolve) => {
      this.fortigateService.findByStoreNumber(this.storeNumber).then(response => {
        try {
          response.toPromise().then(fortigates_from_response => {
            let response_data = fortigates_from_response["message"];
            if (response_data != null) {
              this.fortigate = response_data[0];
            }
          });
        } catch (error) {
          console.log("Fortigates timed out!")
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getWanInterfaces() {
    const promise = new Promise((resolve) => {
      this.fortigateService.getWanInterfaces(this.storeNumber).then(response => {
        try {
          response.toPromise().then(jsonResponse => {
            if (jsonResponse?.["status"] != 200) {
              this.wanInterfaceDataAvailable = false;
              return;
            }
            this.wanInterfacesByHostname = jsonResponse["message"] || {};
            this.wanInterfaceDataAvailable = (jsonResponse["unavailable"] || []).length === 0;
          });
        } catch (error) {
          console.log("Fortigate WAN interfaces timed out!")
          this.wanInterfaceDataAvailable = false;
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getGlobalStatistics() {
    const promise = new Promise((resolve) => {
      this.fortigateService.getRouterGlobalStatistics(this.storeNumber).then(response => {
        try {
          response.toPromise().then(jsonResponse => {
            this.fortigateGlobalStatistics = jsonResponse["message"];
          });
        } catch (error) {
          console.log("Fortigate global statistics timed out!")
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getPrimaryVpnStatus() {
    const promise = new Promise((resolve) => {
      this.fortigateService.getPrimaryVpnStatus(this.storeNumber).then(response => {
        try {
          response.toPromise().then(jsonResponse => {
            this.primaryVpnStatus = jsonResponse["message"];
          });
        } catch (error) {
          console.log("Fortigate primary VPN status timed out!")
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getCradlepointLanAvailability() {
    const promise = new Promise((resolve) => {
      this.cradlepointService.getLanAvailability(this.storeNumber).then(response => {
        try {
          response.toPromise().then(jsonResponse => {
            this.cradlepointLanAvailability = jsonResponse["message"];
          });
        } catch (error) {
          console.log("Cradlepoint LAN availability timed out!")
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getInterfaceThroughput() {
    const promise = new Promise((resolve, reject) => {
      this.wanService.getInterfaceThroughput(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            let response_data = response["message"];
            if (response_data != null) {
              this.interfaceThroughput = response_data
              for (var myInterface in this.interfaceThroughput) {
                if (this.interfaceThroughput.hasOwnProperty(myInterface)) {
                  this.interfaceThroughput[myInterface]["in"] = Math.round(this.interfaceThroughput[myInterface]["in"] / 1000000 * 100) / 100;
                  this.interfaceThroughput[myInterface]["out"] = Math.round(this.interfaceThroughput[myInterface]["out"] / 1000000 * 100) / 100;
                }
              }
            }
          } catch (error) {
            console.log(error);
            console.log("Interface throughput timed out");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getPosServices() {
    const promise = new Promise((resolve) => {
      this.wanService.getPosServices(this.storeNumber).then(response => {
        try {
          response.toPromise().then(response => {
            let response_data = response["message"];
            // for each loop wasnt working, dont know why entirely
            for (let key in response["message"]) { 
              let service: PosService = response_data[key];
              service.name = key;
              if (service.Status == '1') {
                service.Status = 'Online';
              } else {
                service.Status = 'Offline';
              }
              this.posServices.push(service);
            };
            this.checkPosServices();
          });
        } catch (error) {
          console.log("POS Services timed out");
        } finally {
          resolve(true);
        }
      });
    });
    return promise;
  }

  getMerakiSeverity(): 'green' | 'yellow' | 'red' | 'neutral' {
    if (this.merakiSwitchStatuses.length === 0) return 'neutral';
    const offline = this.merakiSwitchStatuses.filter(s => s.status !== 'online').length;
    if (offline === 0) return 'green';
    if (offline === this.merakiSwitchStatuses.length) return 'red';
    return 'yellow';
  }

  getRouterSeverity(): 'green' | 'yellow' | 'red' | 'neutral' {
    let hasDevice = false;
    let hasIssue = false;
    let hasWarn = false;
    if (this.fortigate != null) {
      hasDevice = true;
      if (this.fortigate.conn_status === 2) hasIssue = true;
      else if (this.fortigate.conn_status === 0) hasWarn = true;
    }
    this.cradlepoints.forEach(cradlepoint => {
      hasDevice = true;
      if (cradlepoint.state !== 'online') hasIssue = true;
    });
    if (this.posServices.length > 0) {
      hasDevice = true;
      if (!this.isPosServiceOnline) hasIssue = true;
    }
    if (!hasDevice) return 'neutral';
    if (hasIssue) return 'red';
    if (hasWarn) return 'yellow';
    return 'green';
  }

  checkPosServices() {
    this.posServices.forEach((service: PosService) => {
      if (service.Status !== "Offline") {
        this.isPosServiceOnline = true;
      } else {
        this.isPosServiceOnline = false;
      }
    })
    
  }

  resetView() {
    this.merakiSwitchStatuses = [];
    this.portStatuses = null;
    this.fortigate = null;
    this.cradlepoints = [];
    this.fortigateGlobalStatistics = null;
    this.primaryVpnStatus = null;
    this.cradlepointLanAvailability = null;
    this.interfaceThroughput = null;
    this.posServices = [];
    this.networkState = null;
    this.networkStateEvaluation = null;
    this.wanInterfacesByHostname = {};
    this.wanInterfaceDataAvailable = true;
  }
}